import { Box, Grid, IconButton, InputAdornment, Switch, TextField, Tooltip, Typography } from '@mui/material'
import { AxiosError } from 'axios'
import { nanoid } from 'nanoid'
import React, { useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { UseMutationResult } from 'react-query'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import { getReadablePaymentMethod } from '../../../domain/Adopters/AccountDisplay'
import {
  animalAdoptionOfferTypeOrder,
  getAdoptionAmountDisplay,
  getReadableAnimalAdoptionOfferType,
} from '../../../domain/Animal/AnimalDisplay'
import { AnimalsClient } from '../../../interactors/clients/AnimalsClient'
import {
  AdoptionAttemptDto,
  AdoptionAttemptDtoPaymentMethod,
  AnimalAdoptionDtoStatus,
  AnimalAdoptionDtoOfferType,
  AnimalDto,
} from '../../../interactors/gen/backendClient'
import { useAccountsStore } from '../../../store/AccountsStore'
import { isDoneOutsidePetsoAttempt } from '../../../utils/adoptionAttemptOrigin'
import { useFormExtended } from '../../../utils/hooks/useFormExtended'
import { AnimalCardSaveButton } from '../../Animals/Details/AnimalCardSaveButton'
import { CollapsableCard } from '../../common/CollapsableCard'
import { ColoredChipSelectField } from '../../common/ColoredChipSelectField'
import { ControlledSelectField } from '../../common/ControlledSelectField'
import { colors } from '../../theme'
import { StripeConnectButton } from '../StripeConnectButton'
import { ControlledDateField } from '../../common/ControlledDateField'
import { isValidPastDateCoherence } from '../../../utils/date/isValidPastDateCoherence'
import { ReviveAdopterPopUp } from './ReviveAdopterPopUp'

interface FormParams {
  offerType?: AnimalAdoptionDtoOfferType
  priceInEuros?: number | string
  paymentMethod?: AdoptionAttemptDtoPaymentMethod
  paymentInstallments?: number
  paymentDone: string
  checkNumber?: string
  bankName?: string
  depositDate?: string
  depositAmount?: number | string
  depositStatus?: string
  hasDeposit: boolean
}

interface Props {
  animal: AnimalDto
  adoptionAttempt: AdoptionAttemptDto
  adopterId: string
  isReader: boolean
  updateAttemptMutation: UseMutationResult<
    AdoptionAttemptDto,
    Error | AxiosError<unknown, unknown>,
    AdoptionAttemptDto,
    unknown
  >
  setAdoptionAttempt: (adoptionAttempt: AdoptionAttemptDto) => void
  defaultOpen?: boolean
}

export const ContributionCard: React.FC<Props> = ({
  animal,
  adoptionAttempt,
  adopterId,
  isReader,
  updateAttemptMutation,
  defaultOpen,
}) => {
  const [isLoading, setIsLoading] = React.useState(false)
  /* const isUpMdWidth = useMediaQuery(theme.breakpoints.up('md'))
  const isDownMdWidth = useMediaQuery(theme.breakpoints.down('md')) */

  const getDefaultValues = (attempt: AdoptionAttemptDto): FormParams => ({
    offerType: animal.adoption.offerType || undefined,
    priceInEuros: animal.adoption.priceInEuros || undefined,
    paymentMethod: attempt?.paymentMethod || undefined,
    paymentInstallments: attempt?.paymentInstallments || undefined,
    paymentDone: attempt?.paymentDone ? 'true' : 'false',
    checkNumber: attempt?.checkNumber || undefined,
    bankName: attempt?.bankName || undefined,
    depositDate: attempt?.depositDate || undefined,
    depositAmount: attempt?.depositAmount || undefined,
    depositStatus: attempt?.depositStatus ? 'true' : 'false',
    hasDeposit: Boolean(attempt?.depositDate || attempt?.depositAmount),
  })

  const accountsStore = useAccountsStore()
  const isAdmin = accountsStore.isAdmin()

  const normalizeNumber = (value?: number | string | null): number | undefined => {
    if (value == null || value === '') {
      return undefined
    }

    const normalized = Number(value)
    return Number.isNaN(normalized) ? undefined : normalized
  }

  const roundEuroAmount = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100

  const formatEuroAmount = (value: number): string =>
    `${roundEuroAmount(value).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}€`

  const normalizeText = (value?: string | null): string | undefined => {
    if (value == null) {
      return undefined
    }

    const trimmed = value.trim()
    return trimmed === '' ? undefined : trimmed
  }

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useFormExtended(adoptionAttempt, getDefaultValues)

  const onSubmit = async (data: FormParams) => {
    setIsLoading(true)

    try {
      const nextOfferType = data.offerType ?? undefined
      const nextAnimalPrice = normalizeNumber(data.priceInEuros)
      const currentAnimalPrice = normalizeNumber(animal.adoption.priceInEuros)

      if (nextOfferType !== (animal.adoption.offerType ?? undefined) || nextAnimalPrice !== currentAnimalPrice) {
        animal.adoption.offerType = nextOfferType
        animal.adoption.priceInEuros = nextAnimalPrice
        await AnimalsClient.editAnimal(animal)
      }

      const nextPaymentDone = data.paymentDone === 'true'
      const nextDepositDate = data.hasDeposit ? data.depositDate ?? undefined : undefined
      const nextDepositAmount = data.hasDeposit ? normalizeNumber(data.depositAmount) : undefined
      const nextDepositStatus =
        data.hasDeposit && data.depositStatus != null ? data.depositStatus === 'true' : undefined
      const nextPaymentMethod = nextPaymentDone ? data.paymentMethod ?? undefined : undefined
      const nextPaymentInstallments = nextPaymentDone ? normalizeNumber(data.paymentInstallments) : undefined
      const nextCheckNumber =
        nextPaymentDone && nextPaymentMethod === AdoptionAttemptDtoPaymentMethod.Check
          ? normalizeText(data.checkNumber)
          : undefined
      const nextBankName =
        nextPaymentDone && nextPaymentMethod === AdoptionAttemptDtoPaymentMethod.Check
          ? normalizeText(data.bankName)
          : undefined

      const currentHasDeposit = Boolean(adoptionAttempt.depositDate || adoptionAttempt.depositAmount)
      const currentPaymentDone = adoptionAttempt.paymentDone === true
      const currentDepositDate = currentHasDeposit ? adoptionAttempt.depositDate ?? undefined : undefined
      const currentDepositAmount = currentHasDeposit ? normalizeNumber(adoptionAttempt.depositAmount) : undefined
      const currentDepositStatus = currentHasDeposit ? adoptionAttempt.depositStatus ?? undefined : undefined
      const currentPaymentMethod = currentPaymentDone ? adoptionAttempt.paymentMethod ?? undefined : undefined
      const currentPaymentInstallments = currentPaymentDone
        ? normalizeNumber(adoptionAttempt.paymentInstallments)
        : undefined
      const currentCheckNumber =
        currentPaymentDone && currentPaymentMethod === AdoptionAttemptDtoPaymentMethod.Check
          ? normalizeText(adoptionAttempt.checkNumber)
          : undefined
      const currentBankName =
        currentPaymentDone && currentPaymentMethod === AdoptionAttemptDtoPaymentMethod.Check
          ? normalizeText(adoptionAttempt.bankName)
          : undefined

      const shouldAddPaymentReceptionEvent = nextPaymentDone === true && currentPaymentDone === false
      const shouldAddPaymentCancelledEvent = nextPaymentDone === false && currentPaymentDone === true

      const attemptHasChanged =
        nextPaymentDone !== currentPaymentDone ||
        nextDepositDate !== currentDepositDate ||
        nextDepositAmount !== currentDepositAmount ||
        nextDepositStatus !== currentDepositStatus ||
        nextPaymentMethod !== currentPaymentMethod ||
        nextPaymentInstallments !== currentPaymentInstallments ||
        nextCheckNumber !== currentCheckNumber ||
        nextBankName !== currentBankName ||
        shouldAddPaymentReceptionEvent ||
        shouldAddPaymentCancelledEvent

      if (!attemptHasChanged) {
        return
      }

      const nextEvents = [...(adoptionAttempt.events || [])]
      if (shouldAddPaymentReceptionEvent) {
        nextEvents.push({ id: nanoid(), type: 'payment-reception', date: new Date().toISOString() })
      }

      if (shouldAddPaymentCancelledEvent) {
        nextEvents.push({ id: nanoid(), type: 'payment-cancelled', date: new Date().toISOString() })
      }

      const nextAttempt: AdoptionAttemptDto = {
        ...adoptionAttempt,
        paymentDone: nextPaymentDone,
        depositDate: nextDepositDate,
        depositAmount: nextDepositAmount,
        depositStatus: nextDepositStatus,
        paymentMethod: nextPaymentMethod,
        paymentInstallments: nextPaymentInstallments,
        checkNumber: nextCheckNumber,
        bankName: nextBankName,
        events: nextEvents,
      }

      await updateAttemptMutation.mutateAsync(nextAttempt)
    } finally {
      setIsLoading(false)
    }
  }

  /* const handleCopyLink = () => {
    if (adoptionAttempt.paymentLink) {
      navigator.clipboard.writeText(adoptionAttempt.paymentLink)
      alert('Lien de paiement copié dans le presse-papiers.')
    }
  }

  const handleGenerateLink = async () => {
    if (animal.adoption.priceInEuros) {
      const response = await backendClient.post('/stripe/create-adoption-payment-link', {
        adopterId: adopterId,
        price: (Number(animal.adoption.priceInEuros) + 12) * 100,
        attemptId: adoptionAttempt?.id as string,
      })
      setAdoptionAttempt({ ...adoptionAttempt, paymentLink: response.data })
    }
  } */

  const isDonation = watch('offerType') === AnimalAdoptionDtoOfferType.Donation
  useEffect(() => {
    if (isDonation) {
      setValue('priceInEuros', 0, { shouldValidate: true })
    }
  }, [isDonation, setValue])

  const paymentDone = watch('paymentDone')
  const paymentMethod = watch('paymentMethod')
  const hasDeposit = watch('hasDeposit')
  const depositAmount = watch('depositAmount')
  const depositStatus = watch('depositStatus')
  const adoptionPrice = watch('priceInEuros')
  const isFreeAmount = watch('offerType') === AnimalAdoptionDtoOfferType.FreeAmount
  useEffect(() => {
    if (
      isFreeAmount &&
      (adoptionPrice === undefined || adoptionPrice === null || adoptionPrice === '' || Number(adoptionPrice) < 1)
    ) {
      setValue('priceInEuros', 1, { shouldValidate: true })
    }
  }, [adoptionPrice, isFreeAmount, setValue])
  const normalizedDepositAmount = normalizeNumber(depositAmount)
  const normalizedAdoptionPrice =
    adoptionPrice == null || Number.isNaN(Number(adoptionPrice)) ? undefined : Number(adoptionPrice)
  const effectiveMinimumAmount = isFreeAmount
    ? Math.max(normalizedAdoptionPrice ?? 1, depositStatus === 'true' ? normalizedDepositAmount ?? 0 : 0)
    : normalizedAdoptionPrice
  const leftToPay =
    paymentDone === 'true'
      ? 0
      : normalizedDepositAmount && depositStatus === 'true' && effectiveMinimumAmount != null
      ? Math.max(effectiveMinimumAmount - normalizedDepositAmount, 0)
      : effectiveMinimumAmount
  const paidOnlineAmount = normalizeNumber(adoptionAttempt.paymentAmountInEuros)
  const validatedDepositAmount = hasDeposit && depositStatus === 'true' ? normalizedDepositAmount ?? 0 : 0
  const freeAmountTotalPaid =
    isFreeAmount && paymentDone === 'true' && paidOnlineAmount != null
      ? roundEuroAmount(paidOnlineAmount + validatedDepositAmount)
      : undefined
  const freeAmountPaidDelta =
    freeAmountTotalPaid != null && effectiveMinimumAmount != null
      ? roundEuroAmount(freeAmountTotalPaid - effectiveMinimumAmount)
      : undefined
  const shouldShowFreeAmountPaymentSummary = isFreeAmount && freeAmountTotalPaid != null && freeAmountPaidDelta != null
  const shouldShowPaymentReminder =
    !isDonation &&
    paymentDone !== 'true' &&
    typeof leftToPay === 'number' &&
    leftToPay > 0 &&
    !isReader &&
    Boolean(adopterId)

  const isPastAdoptionOutsidePetso = isDoneOutsidePetsoAttempt(adoptionAttempt)
  const shouldShowStripeConnect =
    !accountsStore.connectedCustomer?.stripeAccountId &&
    animal.adoption.status !== AnimalAdoptionDtoStatus.HasBeenAdopted
  const isDepositToggleLocked = normalizedDepositAmount != null
  const contributionStatus = (() => {
    const offerType = watch('offerType')
    const isIncomplete =
      offerType == null ||
      (!isFreeAmount && (normalizedAdoptionPrice == null || (!isDonation && normalizedAdoptionPrice === 0)))

    if (isIncomplete) {
      return isPastAdoptionOutsidePetso ? { label: 'À compléter', color: '#F8E9C7' } : undefined
    }

    if (isDonation) {
      return { label: 'Don', color: '#EEF2F7' }
    }

    if (paymentDone === 'true') {
      return {
        label: `${getAdoptionAmountDisplay(
          offerType,
          normalizedAdoptionPrice,
          adoptionAttempt.paymentAmountInEuros
        )} - Validé`,
        color: '#DDEFE5',
      }
    }

    if (paymentDone !== 'true' && hasDeposit && depositStatus === 'true') {
      return {
        label: `${getAdoptionAmountDisplay(offerType, normalizedAdoptionPrice)} - acompte reçu`,
        color: '#DCEAF8',
      }
    }

    return {
      label: `${getAdoptionAmountDisplay(offerType, normalizedAdoptionPrice)} - En attente`,
      color: '#FFF1CC',
    }
  })()

  return (
    <CollapsableCard
      defaultExpanded={defaultOpen}
      title="Contribution"
      statusLabel={contributionStatus?.label}
      statusColor={contributionStatus?.color}
    >
      <Box
        sx={{
          mt: 0.25,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: colors.cello,
            fontSize: { xs: '13px', sm: '13.5px' },
            fontStyle: 'italic',
          }}
        >
          Un acompte doit-il être versé ?
        </Typography>
        <Tooltip title="Si un acompte est prévu, il reste géré hors paiement en ligne Petso. L'adoptant pourra payer en ligne uniquement le reste à charge, ou la totalité tant que l'acompte n'a pas été validé.">
          <IconButton size="small" sx={{ color: colors.cello, p: 0.35 }}>
            <HelpOutlineRoundedIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>
        <Controller
          name="hasDeposit"
          control={control}
          render={({ field }) => (
            <Tooltip title={isDepositToggleLocked ? "Un montant d'acompte est actuellement renseigné." : ''}>
              <span>
                <Switch
                  checked={Boolean(field.value || isDepositToggleLocked)}
                  onChange={(e) => field.onChange(e.target.checked)}
                  color="primary"
                  size="small"
                  disabled={isDepositToggleLocked}
                />
              </span>
            </Tooltip>
          )}
        />
      </Box>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          marginTop: 16,
          width: '100%',
        }}
      >
        <Grid container spacing={2}>
          {/* Payment Section */}
          <Grid item xs={12}>
            {
              shouldShowStripeConnect && (
                <Tooltip title={!isAdmin ? 'Seuls les administrateurs peuvent configurer Stripe' : ''}>
                  <span>
                    <StripeConnectButton
                      sx={{ width: '100%', fontWeight: 'bold', textTransform: 'none', borderRadius: 4 }}
                    />
                  </span>
                </Tooltip>
              ) /* : !adoptionAttempt.paymentDone ? (
              adoptionAttempt.paymentLink ? (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCopyLink}
                  sx={{
                    width: '100%',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    borderRadius: 4,
                    marginBottom: 1,
                  }}
                >
                  Copier le lien de paiement
                </Button>
              ) : (
                <Tooltip
                  title={
                    !isAdmin
                      ? 'Seuls les administrateurs peuvent générer un lien de paiement'
                      : animal.adoption.priceInEuros
                      ? ''
                      : 'Veuillez définir un montant avant de générer le lien de paiement'
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleGenerateLink}
                      sx={{
                        width: '100%',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        borderRadius: 4,
                        marginBottom: 1,
                      }}
                      disabled={!isAdmin || !animal.adoption.priceInEuros}
                    >
                      Générer un lien de paiement
                    </Button>
                  </span>
                </Tooltip>
              )
            ) : null */
            }
            {shouldShowPaymentReminder && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <ReviveAdopterPopUp
                  animalId={animal.id}
                  adopterId={adopterId}
                  animalName={animal.name}
                  customerName={accountsStore.connectedCustomer?.name || 'Petso'}
                  buttonLabel="Relancer l'adoptant pour le règlement"
                />
              </Box>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <ControlledSelectField
              control={control}
              error={errors.offerType}
              fieldName="offerType"
              label="Nature de l'offre"
              options={animalAdoptionOfferTypeOrder.map((field: AnimalAdoptionDtoOfferType) => ({
                label: getReadableAnimalAdoptionOfferType(field),
                value: field,
              }))}
              requiredRule=""
              size="small"
            />
            {hasDeposit && (
              <ControlledDateField
                control={control}
                error={errors.depositDate}
                fieldName="depositDate"
                label="Date de l'acompte"
                requiredRule=""
                size="small"
                sx={{ marginTop: 2 }}
                validate={(value) => isValidPastDateCoherence(value) || value === null}
              />
            )}
            {paymentDone === 'true' && (
              <ControlledSelectField
                control={control}
                error={errors.paymentMethod}
                fieldName="paymentMethod"
                label="Mode de réglement"
                options={Object.values(AdoptionAttemptDtoPaymentMethod).map(
                  (field: AdoptionAttemptDtoPaymentMethod) => ({
                    label: getReadablePaymentMethod(field),
                    value: field,
                  })
                )}
                sx={{ marginTop: 2 }}
                requiredRule=""
                size="small"
              />
            )}
            {paymentMethod === AdoptionAttemptDtoPaymentMethod.Check && paymentDone === 'true' && (
              <TextField
                id="check-number-input"
                label="Numéro de chèque"
                fullWidth
                {...register('checkNumber')}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ marginTop: 2 }}
              />
            )}
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <TextField
              id="price-input"
              label={isFreeAmount ? 'Montant minimum (min. 1€)' : 'Montant'}
              type="number"
              inputProps={{
                step: '0.01',
                inputMode: 'decimal',
                pattern: '[0-9]*',
                min: isFreeAmount ? 1 : 0,
              }}
              disabled={isDonation}
              fullWidth
              {...register('priceInEuros', {
                validate: (value) => {
                  if (!isFreeAmount) return true
                  const normalized = Number(value)
                  return normalized >= 1 || 'Le montant minimum doit être au moins de 1€'
                },
              })}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            {hasDeposit && (
              <TextField
                id="price-input"
                label="Montant de l'acompte"
                type="number"
                inputProps={{
                  step: '0.01',
                  inputMode: 'decimal',
                  pattern: '[0-9]*',
                }}
                disabled={isDonation}
                fullWidth
                {...register('depositAmount')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ marginTop: 2.3 }}
                size="small"
              />
            )}
            {paymentDone === 'true' && (
              <ControlledSelectField
                control={control}
                error={errors.paymentInstallments}
                fieldName="paymentInstallments"
                label="Echelonnage du paiement"
                options={[1, 2, 3, 4, 5].map((field: number) => ({
                  label: `${field.toString()} fois`,
                  value: field,
                }))}
                sx={{ marginTop: 2.4 }}
                requiredRule=""
                size="small"
              />
            )}
            {paymentMethod === AdoptionAttemptDtoPaymentMethod.Check && paymentDone === 'true' && (
              <TextField
                id="bank-name-input"
                label="Nom de la banque"
                fullWidth
                {...register('bankName')}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ marginTop: 2 }}
              />
            )}
          </Grid>
          {/* {isUpMdWidth && (
            <Grid item md={3}>
              <PaymentInfoCard adoptionAttempt={adoptionAttempt} animal={animal} isReader={isReader} />
            </Grid>
          )} */}
          <Grid item xs={6} md={3}>
            <ColoredChipSelectField
              control={control}
              error={errors.paymentDone}
              fieldName="paymentDone"
              label="État du paiement"
              options={[
                { label: 'Validé', value: 'true', color: colors.seaGreen },
                { label: 'En attente', value: 'false', color: colors.amber },
              ]}
              requiredRule=""
              size="small"
            />
            {hasDeposit && (
              <ColoredChipSelectField
                control={control}
                error={errors.depositStatus}
                fieldName="depositStatus"
                label="État de l'acompte"
                options={[
                  { label: 'Validé', value: 'true', color: colors.seaGreen },
                  { label: 'En attente', value: 'false', color: colors.amber },
                ]}
                requiredRule=""
                sx={{ marginTop: 2 }}
                size="small"
              />
            )}
          </Grid>
          <Grid item xs={6} md={3} sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <TextField
              id="price-input"
              label={isFreeAmount ? 'Montant min. restant à payer' : 'Montant restant à payer'}
              value={`${leftToPay}€`}
              fullWidth
              inputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ cursor: 'disabled', input: { cursor: 'not-allowed' }, mb: 0.4 }}
              size="small"
            />
          </Grid>
          {shouldShowFreeAmountPaymentSummary && (
            <Grid item xs={12}>
              <Box
                sx={{
                  border: `1px solid ${colors.seaGreen}`,
                  backgroundColor: '#F0FAF4',
                  borderRadius: 2,
                  px: 2,
                  py: 1.25,
                }}
              >
                <Typography sx={{ color: colors.cello, fontWeight: 700, fontSize: 14 }}>
                  L&apos;adoptant a versé {formatEuroAmount(freeAmountTotalPaid)} pour l&apos;adoption
                  {freeAmountPaidDelta > 0
                    ? `, soit +${formatEuroAmount(freeAmountPaidDelta)} par rapport au minimum demandé`
                    : ', soit le minimum demandé'}
                  {validatedDepositAmount > 0 ? ' (acompte inclus).' : '.'}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <AnimalCardSaveButton disabled={!isDirty} isLoading={isLoading} />
          </Grid>
          {/* {isDownMdWidth && (
            <Grid item xs={12}>
              <PaymentInfoCard adoptionAttempt={adoptionAttempt} animal={animal} isReader={isReader} />
            </Grid>
          )} */}
        </Grid>
      </form>
    </CollapsableCard>
  )
}
